import React, { useState, useEffect } from 'react';
import { Table, Button, Badge, Card, Row, Col, Form, InputGroup, Alert, Tab, Tabs } from 'react-bootstrap';
import { Search, Eye, RefreshCw, Package, Calendar, Truck, FileText, Printer } from 'lucide-react';
import useAuth from '../../hooks/useAuth';
import { Document, Page, Text, View, StyleSheet, Image, pdf } from '@react-pdf/renderer';
import autoTable from 'jspdf-autotable';
import { getAllDeliveryNotes } from '../../api/orders';

const DeliveryNoteList = ({ onViewDeliveryNote, onCreateDeliveryNote }) => {
    const { isAdmin, isManager, isEmployee, user } = useAuth();
    const [deliveryNotes, setDeliveryNotes] = useState([]);
    const [filteredDeliveryNotes, setFilteredDeliveryNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [selectedStatus, setSelectedStatus] = useState('ALL');

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://apps.prod.atlanticds-sn.com/senoscar';
    
    // const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://localhost:8080';

    const styles = StyleSheet.create({
        page: {
            fontFamily: 'Helvetica',
            padding: 40,
            fontSize: 12,
        },
        header: {
            flexDirection: 'row',
            marginBottom: 40,
        },
        logo: {
            width: 150,
            height: 'auto',
            marginRight: 20,
        },
        companyInfo: {
            flex: 1,
            textAlign: 'right',
            fontSize: 10,
            color: '#333',
        },
        companyName: {
            fontSize: 12,
            fontWeight: 'bold',
            marginBottom: 5,
        },
        companyAddress: {
            marginBottom: 2,
        },
        title: {
            fontSize: 16,
            fontWeight: 'bold',
            marginBottom: 5,
        },
        dateContainer: {
            alignItems: 'flex-end',
            marginBottom: 20,
        },
        clientSection: {
            marginBottom: 20,
        },
        sectionTitle: {
            fontSize: 12,
            fontWeight: 'bold',
            marginBottom: 5,
            backgroundColor: '#e0e0e0',
            padding: 4,
        },
        clientDetails: {
            fontSize: 10,
            marginBottom: 2,
        },
        table: {
            marginBottom: 30,
        },
        tableHeader: {
            flexDirection: 'row',
            backgroundColor: '#b5835a', // Couleur bronze/dorée pour l'en-tête
            color: 'white',
            padding: 5,
            fontSize: 10,
            fontWeight: 'bold',
            borderBottomWidth: 1,
            borderBottomColor: '#dddddd',
            textAlign: 'center',
        },
        tableRow: {
            flexDirection: 'row',
            borderBottomWidth: 1,
            borderBottomColor: '#dddddd',
            padding: 5,
            fontSize: 10,
        },
        tableCell: {
            padding: 3,
        },
        numCell: {
            width: '5%',
            textAlign: 'center',
        },
        productCell: {
            width: '35%',
        },
        imageCell: {
            width: '15%',
            alignItems: 'center',
        },
        quantityCell: {
            width: '15%',
            textAlign: 'center',
        },
        priceCell: {
            width: '15%',
            textAlign: 'right',
        },
        totalCell: {
            width: '15%',
            textAlign: 'right',
        },
        productImage: {
            width: 40,
            height: 40,
            objectFit: 'contain',
        },
        totalRow: {
            flexDirection: 'row',
            marginTop: 10,
            borderTopWidth: 1,
            borderTopColor: '#dddddd',
            padding: 5,
        },
        netPayableRow: {
            flexDirection: 'row',
            marginTop: 5,
            padding: 5,
            fontWeight: 'bold',
        },
        totalLabel: {
            textAlign: 'left',
            fontWeight: 'bold',
            width: '55%', // Somme des largeurs des 3 premières colonnes (5% + 35% + 15%)
        },
        totalValue: {
            width: '15%',
            textAlign: 'right',
        },
        signatureSection: {
            flexDirection: 'row',
            justifyContent: 'space-between',
            marginTop: 40,
            marginBottom: 20,
        },
        // signatureBox: {
        //     width: '45%', // Pour laisser un peu d'espace entre les deux
        // },
        signatureTextR: {
            fontSize: 10,
            marginBottom: 10,
            textAlign: 'right',
        },
        signatureTextL: {
            fontSize: 10,
            marginBottom: 10,
            textAlign: 'left',
        },
        signatureLine: {
            borderBottomWidth: 1,
            borderBottomColor: '#000',
            paddingBottom: 5,
            textAlign: 'center',
        },
        footer: {
            position: 'absolute',
            bottom: 30,
            left: 40,
            right: 40,
            textAlign: 'center',
            fontSize: 9,
            color: '#666',
            borderTopWidth: 1,
            borderTopColor: '#dddddd',
            paddingTop: 10,
        }
    });
    
    // Fonction identique à celle de GenererPdfFacture pour obtenir l'URL de l'image principale
    const getMainImageUrl = (product) => {
        if (!product.images || product.images.length === 0) {
            return null;
        }
    
        const mainImage = product.images.find(img => img.mainImage);
        const path = mainImage ? mainImage.path : product.images[0].path;
    
        // Traiter l'URL correctement selon le format stocké en base
        if (path.startsWith('http://') || path.startsWith('https://')) {
            return path;
        }
    
        return `${API_BASE_URL}/uploads/${path}`;
    };
    


    useEffect(() => {
        const fetchDeliveryNotes = async () => {
            try {
                setLoading(true);
                setError('');

                const response = await getAllDeliveryNotes();
                const sortedData = [...response.data].sort((a, b) => {
    
                    return b.id - a.id;
                });
                setDeliveryNotes(sortedData);
            } catch (err) {
                setError('Échec du chargement des bons de livraison. Veuillez réessayer.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };

        fetchDeliveryNotes();

    }, [refreshTrigger, isAdmin, isManager]);

    console.log('delivery notes ', deliveryNotes)


    useEffect(() => {
        let filtered = Array.isArray(deliveryNotes) ? [...deliveryNotes] : [];

        // Filtre par terme de recherche
        if (searchTerm.trim() !== '') {
            filtered = filtered.filter(note =>
                note.referenceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                note.orderReference?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                note.clientName?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filtre par statut
        if (selectedStatus !== 'ALL') {
            filtered = filtered.filter(note => note.status === selectedStatus);
        }

        setFilteredDeliveryNotes(filtered);
    }, [deliveryNotes, searchTerm, selectedStatus]);

    const handleRefresh = () => {
        setRefreshTrigger(prev => prev + 1);
    };

   
    // Composant PDF pour les bons de livraison
const DeliveryNotePDF = ({ deliveryNote }) => {
    // Calculer la quantité totale commandée et livrée
    const totalOrderedQuantity = deliveryNote.items.reduce((sum, item) => sum + item.deliveredQuantity, 0);
    const totalMontant = deliveryNote.items.reduce((sum, item) => {
        // Vérifier que deliveredQuantity et unitPrice ne sont pas null/undefined
        const quantity = item.deliveredQuantity || 0;
        const price = item.productUnit || 0;
        
        // Calculer le montant de la ligne et l'ajouter au total
        return sum + (quantity * price);
    }, 0);    
    // Obtenir la date formatée
    const formattedDate = formatDate(deliveryNote.deliveryDate);
    
    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* En-tête avec logo et informations de l'entreprise */}
                <View style={styles.header}>
                    <Image
                        src="/senoscar/images/logoSO.png"
                        style={styles.logo}
                    />
                    <View style={styles.companyInfo}>
                        <Text style={styles.companyName}>Zone Industrielle, Diamniadio,</Text>
                        <Text style={styles.companyAddress}>En Face Du Marché D'intérêt National,</Text>
                        <Text style={styles.companyAddress}>Dakar, Sénégal, Tel: +221 78 655 88 88</Text>
                    </View>
                </View>

                {/* Titre du document et date */}
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 20 }}>
                    <Text style={styles.title}>BON DE LIVRAISON N° {deliveryNote.referenceNumber}</Text>
                    <Text>{formattedDate}</Text>
                </View>
                
                {/* Référence de commande */}
                <View style={{ marginBottom: 10 }}>
                    <Text>Référence commande : {deliveryNote.orderReference}</Text>
                </View>

                {/* Section client */}
                <View style={styles.clientSection}>
                    <Text style={styles.sectionTitle}>CLIENT</Text>
                    <Text style={styles.clientDetails}>{deliveryNote.clientName}</Text>
                    <Text style={styles.clientDetails}>{deliveryNote.clientAddress || "Adresse non spécifiée"}</Text>
                </View>

                {/* Tableau des produits */}
                <View style={styles.table}>
                    {/* En-tête du tableau */}
                    <View style={styles.tableHeader}>
                        <Text style={[styles.tableCell, styles.numCell]}>N°</Text>
                        <Text style={[styles.tableCell, styles.productCell]}>PRODUIT</Text>
                        {/* <Text style={[styles.tableCell, styles.imageCell]}>IMAGE</Text> */}
                        {/* <Text style={[styles.tableCell, styles.quantityCell]}>QTÉ COMMANDÉE</Text> */}
                        <Text style={[styles.tableCell, styles.quantityCell]}>QTÉ LIVRÉE</Text>
                        <Text style={[styles.tableCell, styles.notesCell]}>OBSERVATIONS</Text>
                    </View>

                    {/* Corps du tableau */}
                    {deliveryNote.items.map((item, index) => (
                        <View key={index} style={styles.tableRow}>
                            <Text style={[styles.tableCell, styles.numCell]}>{index + 1}</Text>
                            <Text style={[styles.tableCell, styles.productCell]}>
                                {item.productCode} / {item.productName}
                            </Text>
                            {/* <View style={[styles.tableCell, styles.imageCell]}>
                                {item.product && getMainImageUrl(item.product) && (
                                    <Image
                                        src={getMainImageUrl(item.product)}
                                        style={styles.productImage}
                                    />
                                )}
                            </View> */}
                            {/* <Text style={[styles.tableCell, styles.quantityCell]}>{item.quantity}</Text> */}
                            <Text style={[styles.tableCell, styles.quantityCell]}>{item.deliveredQuantity}</Text>
                            {/* <Text style={[styles.tableCell, styles.unitCell]}>{item.productUnit || 'Unité'}</Text> */}
                            <Text style={[styles.tableCell, styles.notesCell]}>{item.notes || ''}</Text>
                        </View>
                    ))}

                    {/* Ligne des totaux */}
                    <View style={styles.totalRow}>
                        {/* <Text style={styles.totalLabel}>TOTAL</Text>
                        <Text style={[styles.tableCell, styles.quantityCell]}>{totalOrderedQuantity}</Text> */}
                        {/* <Text style={[styles.tableCell, styles.quantityCell]}>{totalMontant}</Text> */}
                        <Text style={[styles.tableCell, styles.unitCell]}></Text>
                        <Text style={[styles.tableCell, styles.notesCell]}></Text>
                    </View>
                </View>

                {/* Section signatures */}
                <View style={styles.signatureSection}>
                    <View style={styles.signatureBox}>
                        <Text style={styles.signatureTextR}>Signature du livreur</Text>
                        {/* <Text style={styles.signatureLine}></Text> */}
                    </View>
                    <View style={styles.signatureBox}>
                        <Text style={styles.signatureTextL}>Signature du client</Text>
                        {/* <Text style={styles.signatureLine}></Text> */}
                    </View>
                </View>

                {/* Notes */}
                {deliveryNote.notes && (
                    <View style={styles.notesSection}>
                        <Text style={styles.notesTitle}>Notes:</Text>
                        <Text style={styles.notesText}>{deliveryNote.notes}</Text>
                    </View>
                )}

                {/* Pied de page */}
                <View style={styles.footer}>
                    <Text>
                        Zone Industrielle, Diamniadio,
                        En Face Du Marché D'intérêt National,
                        Dakar, Sénégal
                    </Text>
                    <Text>Tel: +221 78 655 88 88</Text>
                </View>
            </Page>
        </Document>
    );
};


// Fonction pour générer et télécharger un PDF de bon de livraison
const generateDeliveryNotePDF = async (deliveryNote) => {
    try {
        console.log("Génération du PDF pour le bon de livraison:", deliveryNote.referenceNumber);
        
        // Créer le blob PDF
        const blob = await pdf(
            <DeliveryNotePDF deliveryNote={deliveryNote} />
        ).toBlob();
    
        // Créer une URL pour le blob
        const url = URL.createObjectURL(blob);
        
        // Créer un lien de téléchargement
        const link = document.createElement('a');
        link.href = url;
        link.download = `BonDeLivraison_${deliveryNote.referenceNumber}.pdf`;
        
        // Ajouter temporairement le lien au document, cliquer dessus, puis le supprimer
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Libérer l'URL
        URL.revokeObjectURL(url);
        
        console.log("PDF du bon de livraison généré avec succès");
    } catch (error) {
        console.error("Erreur lors de la génération du PDF du bon de livraison:", error);
        alert("Erreur lors de la génération du PDF du bon de livraison. Veuillez réessayer.");
    }
};


    
    const getStatusBadge = (status) => {
        switch (status) {
            case 'PENDING':
                return <Badge bg="warning">En attente</Badge>;
            case 'IN_TRANSIT':
                return <Badge bg="primary">En transit</Badge>;
            case 'DELIVERED':
                return <Badge bg="success">Livré</Badge>;
            case 'RETURNED':
                return <Badge bg="danger">Retourné</Badge>;
            default:
                return <Badge bg="secondary">{status}</Badge>;
        }
    };

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Card className="shadow-sm">
            <Card.Header>
                <Row className="align-items-center">
                    <Col>
                        <h5 className="mb-0">Gestion des Bons de Livraison</h5>
                    </Col>
                    <Col xs="auto">
                    <Button
                        variant="success"
                        size="sm"
                        className="me-2"
                        onClick={
                            onCreateDeliveryNote
                        }
                    >
                        <FileText size={16} className="me-1" />
                        Nouveau Bon de Livraison
                    </Button>

                        <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={handleRefresh}
                        >
                            <RefreshCw size={16} />
                        </Button>
                    </Col>
                </Row>
            </Card.Header>
            <Card.Body>
                {error && <Alert variant="danger">{error}</Alert>}

                <Row className="mb-3">
                    <Col md={8}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search size={18} />
                            </InputGroup.Text>
                            <Form.Control
                                placeholder="Rechercher par référence, commande ou client..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </InputGroup>
                    </Col>
                    {/* <Col md={4}>
                        <Form.Select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                        >
                            <option value="ALL">Tous les statuts</option>
                            <option value="PENDING">En attente</option>
                            <option value="IN_TRANSIT">En transit</option>
                            <option value="DELIVERED">Livré</option>
                            <option value="RETURNED">Retourné</option>
                        </Form.Select>
                    </Col> */}
                </Row>

                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Chargement...</span>
                        </div>
                        <p className="mt-2">Bons de livraison en chargement...</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped hover>
                            <thead>
                                <tr>
                                    <th>Référence</th>
                                    <th>Commande</th>
                                    <th>Client</th>
                                    <th>Date de livraison</th>
                                    <th>Produits</th>
                                    {/* <th>Statut</th> */}
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredDeliveryNotes.length > 0 ? (
                                    [...filteredDeliveryNotes]
                                        .sort((a, b) => new Date(b.deliveryDate) - new Date(a.deliveryDate))
                                        .map(note => (
                                            <tr key={note.id}>
                                                <td>
                                                    <div className="fw-bold">{note.referenceNumber}</div>
                                                    <small className="text-muted">
                                                        {formatDate(note.createdAt)}
                                                    </small>
                                                </td>
                                                <td>
                                                    <div>{note.orderReference}</div>
                                                </td>
                                                <td>
                                                    <div>{note.clientName}</div>
                                                </td>
                                                <td>
                                                    <div className="small d-flex align-items-center">
                                                        <Calendar size={12} className="me-1" />
                                                        {formatDate(note.deliveryDate)}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <Package size={12} className="me-1" />
                                                        {note.items.length} produits
                                                    </div>
                                                </td>
                                                {/* <td>{getStatusBadge(note.status)}</td> */}
                                                <td>
                                                    <Button
                                                        variant="outline-info"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => onViewDeliveryNote(note.id)}
                                                    >
                                                        <Eye size={16} />
                                                    </Button>
                                                    <Button
                                                        variant="outline-dark"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => generateDeliveryNotePDF(note)}
                                                    >
                                                        <Printer size={16} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))
                                ) : (
                                    <tr>
                                        <td colSpan="7" className="text-center py-3">
                                            Aucun bon de livraison trouvé
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
};

export default DeliveryNoteList;